import React, { useState, useEffect, useMemo } from "react";
import { Box, Text, Grid, Group } from "@mantine/core";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isoWeek from "dayjs/plugin/isoWeek";
import weekday from "dayjs/plugin/weekday";

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
dayjs.extend(weekday);

export type WeekStartDay = 0 | 1; // 0 = Sunday, 1 = Monday

export interface ContentBlockTypeCalendarProps {
  /** Timezone to display the calendar in (e.g., "America/New_York") */
  timezone?: string;

  /** Whether to highlight the current day */
  highlightToday?: boolean;

  /** Whether to show week numbers */
  showWeekNumbers?: boolean;

  /** First day of week: 0 = Sunday, 1 = Monday */
  weekStartsOn?: WeekStartDay;

  /** Additional CSS class name for styling */
  className?: string;

  themeConfig?: any;

  /** Callback fired when the current day changes (at midnight) */
  onDayChange?: ((newDate: string) => void) | undefined;
}

interface CalendarDay {
  date: dayjs.Dayjs;
  isCurrentMonth: boolean;
  isToday: boolean;
  weekNumber?: number;
}

export const ContentBlockTypeCalendar: React.FC<
  ContentBlockTypeCalendarProps
> = ({
  timezone = "UTC",
  highlightToday = true,
  showWeekNumbers = false,
  weekStartsOn = 0,
  className,
  themeConfig,
  onDayChange,
}) => {
  const [currentDate, setCurrentDate] = useState(() =>
    timezone ? dayjs().tz(timezone) : dayjs()
  );

  // Update current date every minute to keep calendar fresh
  useEffect(() => {
    const updateCurrentDate = () => {
      const newDate = timezone ? dayjs().tz(timezone) : dayjs();
      const prevDateStr = currentDate.format("YYYY-MM-DD");
      const newDateStr = newDate.format("YYYY-MM-DD");

      setCurrentDate(newDate);

      // Trigger callback if day has changed
      if (prevDateStr !== newDateStr) {
        onDayChange?.(newDateStr);
      }
    };

    // Update every minute
    const intervalId = setInterval(updateCurrentDate, 60000);

    return () => clearInterval(intervalId);
  }, [timezone, currentDate, onDayChange]);

  // Get calendar days for current month
  const calendarDays = useMemo(() => {
    const startOfMonth = currentDate.startOf("month");
    const endOfMonth = currentDate.endOf("month");

    // Calculate the start date (beginning of the week containing the 1st)
    const startDate = startOfMonth.weekday(weekStartsOn);

    // Calculate the end date (end of the week containing the last day)
    const endDate = endOfMonth.weekday(weekStartsOn + 6);

    const days: CalendarDay[] = [];
    let date = startDate;

    while (date.isBefore(endDate) || date.isSame(endDate, "day")) {
      const isCurrentMonth = date.isSame(currentDate, "month");
      const isToday = date.isSame(currentDate, "day");
      const weekNumber = showWeekNumbers ? date.isoWeek() : undefined;

      days.push({
        date,
        isCurrentMonth,
        isToday,
        weekNumber,
      });

      date = date.add(1, "day");
    }

    return days;
  }, [currentDate, weekStartsOn, showWeekNumbers]);

  // Get weekday labels
  const weekdayLabels = useMemo(() => {
    const labels: string[] = [];
    const startDay = dayjs().weekday(weekStartsOn);

    for (let i = 0; i < 7; i++) {
      labels.push(startDay.add(i, "day").format("ddd"));
    }

    return labels;
  }, [weekStartsOn]);

  // Group days by week
  const weeks = useMemo(() => {
    const weeksArray: CalendarDay[][] = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeksArray.push(calendarDays.slice(i, i + 7));
    }
    return weeksArray;
  }, [calendarDays]);

  console.log("===", themeConfig?.contentBlocks?.calendar);

  return (
    <Box
      className={`content-block-calendar ${className || ""}`}
      style={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        padding: "clamp(0.5rem, 2vh, 1.5rem)",
        overflow: "hidden",
        ...(themeConfig?.contentBlocks?.calendar?.container && {
          ...themeConfig?.contentBlocks?.calendar?.container,
        }),
      }}
    >
      {/* Month and Year Header */}
      <Group
        justify="center"
        gap="xs"
        style={{
          flexShrink: 0,
          marginBottom: "clamp(0.5rem, 1.5vh, 1rem)",
        }}
      >
        <Text
          className="content-block-calendar__month"
          style={{
            textAlign: "center",
            lineHeight: 1,
            ...(themeConfig?.contentBlocks?.calendar?.month && {
              ...themeConfig?.contentBlocks?.calendar?.month,
            }),
            background: "transparent",
          }}
        >
          {currentDate.format("MMMM")}
        </Text>
        <Text
          className="content-block-calendar__year"
          style={{
            textAlign: "center",
            lineHeight: 1,
            ...(themeConfig?.contentBlocks?.calendar?.month && {
              ...themeConfig?.contentBlocks?.calendar?.month,
            }),
            background: "transparent",
          }}
        >
          {currentDate.format("YYYY")}
        </Text>
      </Group>

      {/* Calendar Grid Container */}
      <Box
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {/* Weekday Headers */}
        <Box
          style={{
            flexShrink: 0,
            marginBottom: "clamp(0.25rem, 0.5vh, 0.5rem)",
            borderBottom: "2px solid var(--mantine-color-default-border)",
            paddingBottom: "clamp(0.25rem, 0.5vh, 0.5rem)",
          }}
        >
          <Grid gutter="xs">
            {showWeekNumbers && (
              <Grid.Col span={1}>
                <Box
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "clamp(0.25rem, 0.5vh, 0.5rem)",
                  }}
                >
                  <Text
                    style={{
                      textAlign: "center",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      ...(themeConfig?.contentBlocks?.calendar?.weekdays && {
                        ...themeConfig?.contentBlocks?.calendar?.weekdays,
                      }),
                      background: "transparent",
                    }}
                  >
                    WK
                  </Text>
                </Box>
              </Grid.Col>
            )}
            {weekdayLabels.map((label, index) => (
              <Grid.Col key={index} span={showWeekNumbers ? 11 / 7 : 12 / 7}>
                <Box
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "clamp(0.25rem, 0.5vh, 0.5rem)",
                  }}
                >
                  <Text
                    className="content-block-calendar__weekday"
                    style={{
                      textAlign: "center",
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      ...(themeConfig?.contentBlocks?.calendar?.weekdays && {
                        ...themeConfig?.contentBlocks?.calendar?.weekdays,
                      }),
                      background: "transparent",
                    }}
                  >
                    {label}
                  </Text>
                </Box>
              </Grid.Col>
            ))}
          </Grid>
        </Box>

        {/* Calendar Weeks */}
        <Box
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: "clamp(0.125rem, 0.3vh, 0.25rem)",
            minHeight: 0,
          }}
        >
          {weeks.map((week, weekIndex) => (
            <Box
              key={weekIndex}
              style={{
                flex: 1,
                minHeight: 0,
              }}
            >
              <Grid gutter="xs" style={{ height: "100%" }}>
                {showWeekNumbers && (
                  <Grid.Col span={1}>
                    <Box
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                      }}
                    >
                      <Text
                        style={{
                          textAlign: "center",
                          ...(themeConfig?.contentBlocks?.calendar
                            ?.weeknumbers && {
                            ...themeConfig?.contentBlocks?.calendar
                              ?.weeknumbers,
                          }),
                          background: "transparent",
                        }}
                      >
                        {week[0].weekNumber}
                      </Text>
                    </Box>
                  </Grid.Col>
                )}
                {week.map((day, dayIndex) => {
                  const isHighlighted = highlightToday && day.isToday;

                  return (
                    <Grid.Col
                      key={dayIndex}
                      span={showWeekNumbers ? 11 / 7 : 12 / 7}
                      style={{ height: "100%" }}
                    >
                      <Box
                        className={`content-block-calendar__day ${
                          day.isCurrentMonth
                            ? "content-block-calendar__day--current-month"
                            : "content-block-calendar__day--other-month"
                        } ${
                          isHighlighted
                            ? "content-block-calendar__day--today"
                            : ""
                        }`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          width: "100%",
                          borderRadius: "var(--mantine-radius-md)",
                          ...(isHighlighted &&
                            themeConfig?.contentBlocks?.calendar
                              ?.currentDay && {
                              ...themeConfig?.contentBlocks?.calendar
                                ?.currentDay,
                            }),
                          ...(!isHighlighted &&
                            themeConfig?.contentBlocks?.calendar?.day && {
                              ...themeConfig?.contentBlocks?.calendar?.day,
                            }),
                        }}
                      >
                        <Text
                          style={{
                            fontSize: "inherit",
                            fontWeight: "inherit",
                            color: "inherit",
                            fontFamily: "inherit",
                            textAlign: "center",
                            opacity: day.isCurrentMonth ? 1 : 0.4,
                          }}
                        >
                          {day.date.format("D")}
                        </Text>
                      </Box>
                    </Grid.Col>
                  );
                })}
              </Grid>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};
